import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  LinearProgress,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { candidateAPI } from '../../utils/api';

const AssignmentList = ({ user, onStartTest }) => {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await candidateAPI.getAssignments(user.id);
      setAssignments(data.assignments);
    } catch (err) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const getProgressPercentage = (assignment) => {
    const { completed_questions, total_questions } = assignment.progress;
    return total_questions > 0 ? (completed_questions / total_questions) * 100 : 0;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return t('candidate.assignments.status.completed');
      case 'in_progress':
        return t('candidate.assignments.status.inProgress');
      default:
        return t('candidate.assignments.status.pending');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t('candidate.assignments.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {assignments.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('candidate.assignments.noAssignments')}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {assignments.map((assignment) => (
            <Grid item xs={12} md={6} key={assignment.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      {assignment.assessment.name}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {assignment.assessment.description || 'No description'}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={getStatusLabel(assignment.status).toUpperCase()}
                      color={getStatusColor(assignment.status)}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('candidate.assignments.progress', {
                      completed: assignment.progress.completed_questions,
                      total: assignment.progress.total_questions
                    })}
                  </Typography>

                  <LinearProgress
                    variant="determinate"
                    value={getProgressPercentage(assignment)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />

                  {assignment.status === 'completed' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, color: 'success.main' }}>
                      <CheckCircleIcon sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {t('candidate.assignments.status.completed')}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => onStartTest(assignment)}
                    disabled={assignment.status === 'completed'}
                  >
                    {assignment.status === 'completed'
                      ? t('candidate.assignments.status.completed')
                      : assignment.status === 'in_progress'
                      ? t('candidate.assignments.continueTest')
                      : t('candidate.assignments.startTest')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default AssignmentList;
